import { React, useState, useEffect } from "react";
import { api } from "../../../../../utils/api";
import { useParams } from "react-router-dom";
import { Button, Tabs, Tab, Box, Paper } from "@mui/material";
import TestStoreBadge from "../../../../../components/TestStoreBadge";
import ModalPopup from "../../../../../components/ModalPopup";

import GlobalLoadingSpinner from "../../../../../components/GlobalLoadingSpinner";
import MerchantStoreInfoTable from "../../../../../components/MerchantStoreInfoTable";
import MerchantStoreForm from "../../../../../components/Forms/MerchantStoreForm";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { COLORS } from "../../../../../utils/colors";
import StoreStaff from "./StoreStaff";
import MerchantStorePricingForm from "../../../../MerchantStorePricing/Forms/MerchantStorePricingForm";

function StoreTabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`merchant-store-tabpanel-${index}`}
      aria-labelledby={`merchant-store-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function PricingTabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-pricing-tabpanel-${index}`}
      aria-labelledby={`store-pricing-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MerchantStoreSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [editMerchantStoreState, setEditMerchantStoreState] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [showMerchantStorePricingForm, setShowMerchantStorePricingForm] = useState(false);
  const [showAddOnProductForm, setShowAddOnProductForm] = useState(false);
  const [mainTab, setMainTab] = useState(0);
  const [pricingTab, setPricingTab] = useState(0);

  const isAdministrator = sessionStorage.getItem("selectedPortal") === "Administrator";

  useEffect(() => {
    fetchStoreDetails();
  }, []);

  const fetchStoreDetails = async () => {
    try {
      const storeResponse = await api(`v1/merchant/${params.idMerchant}/store/${params.idStore}`, "GET", null);
      if (storeResponse.data.success) {
        const selectedStore = storeResponse.data.data.merchantStore;

        const pricingResponse = await api(`v1/merchantStorePricing/store/${params.idStore}`, "GET", null);
        if (pricingResponse.data.success) {
          const merchantStorePricingOptions = pricingResponse.data.data.merchantStorePricings;
          const updatedMerchantStorePricingOptions = await Promise.all(
            merchantStorePricingOptions.map(async (merchantStorePricingOption) => {
              try {
                if (!merchantStorePricingOption || !merchantStorePricingOption.product) {
                  return null;
                }
                const productResponse = await api(`v1/product/${merchantStorePricingOption.product}`, "GET");
                if (productResponse.data.success) {
                  const product = productResponse.data.data.product;
                  if (!product) {
                    return null;
                  }
                  return {
                    ...merchantStorePricingOption,
                    product: product.name || "Unknown Product",
                    productType: product.type || null,
                    isAddOnOnly: product.isAddOnOnly || false,
                  };
                }
                return null;
              } catch (error) {
                console.error("API error:", error);
                return null;
              }
            })
          );
          selectedStore.pricing = updatedMerchantStorePricingOptions.filter(
            (pricing) => pricing !== null && pricing !== undefined
          );
        }

        setSelectedStore(selectedStore);
        setLoading(false);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleDeleteStoreConfirm = async () => {
    setLoading(true);
    try {
      const response = await api(`v1/merchant/${params.idMerchant}/store/${params.idStore}`, "DELETE", null);
      if (response.data.success) {
        window.location.replace("/merchant/" + params.idMerchant);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    toggleDisplayDeleteConfirm();
    setLoading(false);
  };

  const toggleDisplayDeleteConfirm = () => {
    setDeleteConfirmationIsOpen(!deleteConfirmationIsOpen);
  };

  const editMerchantStore = () => {
    setEditMerchantStoreState((prevValue) => !prevValue);
  };

  const subscriptionPricings =
    selectedStore?.pricing?.filter((pricing) => pricing && pricing.productType === "subscription") || [];
  const oneTimePricings =
    selectedStore?.pricing?.filter((pricing) => pricing && pricing.productType === "onceoff" && !pricing.isAddOnOnly) ||
    [];
  const addOnPricings = selectedStore?.pricing?.filter((pricing) => pricing && pricing.isAddOnOnly === true) || [];

  const staffCount = selectedStore?.staff?.length ?? 0;
  const pricingCount = selectedStore?.pricing?.length ?? 0;

  const renderPricingCard = (pricing, priceLabel = "Price") => (
    <div
      key={pricing._id}
      className="flex flex-col shadow-md p-4 rounded-md border border-slate-200 w-full max-w-md hover:cursor-pointer hover:border-slate-400 transition-colors"
      onClick={() => navigate(`/merchantStorePricing/${pricing._id}`)}
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="font-semibold text-slate-600">Product</span>
        <span>{pricing.product}</span>
        <span className="font-semibold text-slate-600">{priceLabel}</span>
        <span>${pricing.price}</span>
      </div>
    </div>
  );

  const renderAddOnPricingCard = (pricing) => (
    <div
      key={pricing._id}
      className="flex flex-col shadow-md p-4 rounded-md border border-slate-200 w-full max-w-md hover:cursor-pointer hover:border-slate-400 transition-colors"
      onClick={() => navigate(`/merchantStorePricing/${pricing._id}`)}
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="font-semibold text-slate-600">Product</span>
        <span>{pricing.product}</span>
        <span className="font-semibold text-slate-600">Base Price</span>
        <span>${pricing.price}</span>
        {pricing.vehicleTypeVariants && (
          <>
            {pricing.vehicleTypeVariants.sedan && (
              <>
                <span className="font-semibold text-slate-600">Sedan/Hatch</span>
                <span>${pricing.vehicleTypeVariants.sedan}</span>
              </>
            )}
            {pricing.vehicleTypeVariants.wagon && (
              <>
                <span className="font-semibold text-slate-600">SUV/Wagon</span>
                <span>${pricing.vehicleTypeVariants.wagon}</span>
              </>
            )}
            {pricing.vehicleTypeVariants["4wd"] && (
              <>
                <span className="font-semibold text-slate-600">4WD/7Seater</span>
                <span>${pricing.vehicleTypeVariants["4wd"]}</span>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderPricingSection = () => (
    <>
      {isAdministrator && (
        <div className="mb-4">
          <Button
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            variant="contained"
            onClick={() => setShowMerchantStorePricingForm(true)}
          >
            Add new pricing
          </Button>
          <ModalPopup
            showModal={showMerchantStorePricingForm}
            handleShowModal={() => setShowMerchantStorePricingForm(false)}
            popupTitle="Add New Store Pricing Option"
            popupContent={
              <MerchantStorePricingForm
                callback={fetchStoreDetails}
                cancelModal={() => setShowMerchantStorePricingForm(false)}
                onlyStore={selectedStore._id}
              />
            }
          />
        </div>
      )}

      {pricingCount > 0 ? (
        isAdministrator ? (
          <>
            <Tabs
              value={pricingTab}
              onChange={(_, v) => setPricingTab(v)}
              aria-label="Store pricing by product type"
              sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
            >
              <Tab label={`Subscriptions (${subscriptionPricings.length})`} />
              <Tab label={`One-time (${oneTimePricings.length})`} />
              <Tab label={`Add-ons (${addOnPricings.length})`} />
            </Tabs>
            <PricingTabPanel value={pricingTab} index={0}>
              {subscriptionPricings.length > 0 ? (
                <div className="flex flex-wrap gap-3">{subscriptionPricings.map((p) => renderPricingCard(p))}</div>
              ) : (
                <p className="text-slate-600">No subscription pricing for this store.</p>
              )}
            </PricingTabPanel>
            <PricingTabPanel value={pricingTab} index={1}>
              {oneTimePricings.length > 0 ? (
                <div className="flex flex-wrap gap-3">{oneTimePricings.map((p) => renderPricingCard(p))}</div>
              ) : (
                <p className="text-slate-600">No one-time product pricing for this store.</p>
              )}
            </PricingTabPanel>
            <PricingTabPanel value={pricingTab} index={2}>
              {isAdministrator && (
                <div className="mb-4">
                  <Button
                    sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                    variant="contained"
                    onClick={() => setShowAddOnProductForm(true)}
                  >
                    Add add-on product
                  </Button>
                  <ModalPopup
                    showModal={showAddOnProductForm}
                    handleShowModal={() => setShowAddOnProductForm(false)}
                    popupTitle="Add Add-On Product"
                    popupContent={
                      <MerchantStorePricingForm
                        callback={() => {
                          fetchStoreDetails();
                          setShowAddOnProductForm(false);
                        }}
                        cancelModal={() => setShowAddOnProductForm(false)}
                        onlyStore={selectedStore._id}
                        onlyAddOnProducts={true}
                      />
                    }
                  />
                </div>
              )}
              {addOnPricings.length > 0 ? (
                <div className="flex flex-wrap gap-3">{addOnPricings.map((p) => renderAddOnPricingCard(p))}</div>
              ) : (
                <p className="text-slate-600">No add-on pricing for this store.</p>
              )}
            </PricingTabPanel>
          </>
        ) : (
          <div className="flex flex-wrap gap-3">{selectedStore.pricing.map((p) => renderPricingCard(p))}</div>
        )
      ) : (
        <p className="text-slate-600">No pricing added yet. Add a pricing row to link products to this location.</p>
      )}
    </>
  );

  return (
    selectedStore && (
      <div className="page merchant-store-single flex flex-col items-stretch min-h-screen p-4 md:p-6 gap-4 w-full max-w-5xl mx-auto">
        {loading && <GlobalLoadingSpinner />}
        <ModalPopup
          showModal={editMerchantStoreState}
          handleShowModal={editMerchantStore}
          popupTitle="Edit Merchant Store"
          popupContent={
            <MerchantStoreForm
              merchant={{ _id: params.idMerchant }}
              merchantStore={selectedStore}
              fetchStoreDetails={fetchStoreDetails}
              handleDisplayModal={editMerchantStore}
            />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        <div className="w-full flex flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h2 className="font-bold text-xl md:text-2xl truncate">{selectedStore.name}</h2>
            <TestStoreBadge store={selectedStore} />
          </div>
          <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid", borderColor: "divider", flexShrink: 0 }}>
            <ArrowBackIcon />
          </IconButton>
        </div>

        <Paper elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            aria-label="Merchant store sections"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "grey.50",
            }}
          >
            <Tab label="Details" id="merchant-store-tab-0" aria-controls="merchant-store-tabpanel-0" />
            <Tab
              label={`Staff (${staffCount})`}
              id="merchant-store-tab-1"
              aria-controls="merchant-store-tabpanel-1"
            />
            <Tab
              label={`Pricing (${pricingCount})`}
              id="merchant-store-tab-2"
              aria-controls="merchant-store-tabpanel-2"
            />
          </Tabs>

          <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
            <StoreTabPanel value={mainTab} index={0}>
              <MerchantStoreInfoTable data={selectedStore} />
              <div className="flex flex-wrap gap-3 mt-6">
                <Button
                  sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                  variant="contained"
                  onClick={editMerchantStore}
                >
                  Edit store
                </Button>
                {isAdministrator && (
                  <Button variant="contained" color="error" onClick={toggleDisplayDeleteConfirm}>
                    Delete store
                  </Button>
                )}
              </div>
              <ModalPopup
                showModal={deleteConfirmationIsOpen}
                handleShowModal={toggleDisplayDeleteConfirm}
                popupTitle="Delete Store"
                popupContent={
                  <div>
                    Are you sure you want to delete store: <strong>{selectedStore.name}</strong>?
                  </div>
                }
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
                confirmCallbackAction={handleDeleteStoreConfirm}
              />
            </StoreTabPanel>

            <StoreTabPanel value={mainTab} index={1}>
              {staffCount > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedStore.staff.map((staff) => {
                    const merchantId =
                      params.idMerchant ||
                      (selectedStore.merchantAccount
                        ? typeof selectedStore.merchantAccount === "object"
                          ? selectedStore.merchantAccount._id
                          : selectedStore.merchantAccount
                        : null);
                    return (
                      <StoreStaff
                        key={staff.value}
                        name={staff.label}
                        onClick={() => {
                          if (merchantId) {
                            navigate(`/merchant/${merchantId}/user/${staff.value}`);
                          }
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-600">No staff assigned to this store yet.</p>
              )}
            </StoreTabPanel>

            <StoreTabPanel value={mainTab} index={2}>
              {renderPricingSection()}
            </StoreTabPanel>
          </Box>
        </Paper>
      </div>
    )
  );
};

export default MerchantStoreSingle;
